/**
 * DocumentEditor — compose a draft invoice/quote and save/issue it (design §3).
 * Live India-GST tax engine (intra→CGST+SGST, inter→IGST; discount pre/post;
 * nearest-rupee rounding; FX). Bill-to is a real CRM customer picker. Save draft
 * → createDocument; Issue → createDocument + issueDocument. Variant chip computed;
 * Issue gated on a chosen customer AND role ≠ VIEWER.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import { useSession } from '../context/sessionContext';
import SkIcon from '../components/skIcon';
import { DocRow, initials, fmtMoney } from '../utils/format';
import { showToast } from '../components/skToast';
import IssueModal from '../components/modals/issueModal';
import ModalShell from '../components/modals/modalShell';
import DropdownSelect from '../components/dropdownSelect';
import DateField from '../components/dateField';
import CommonInput from '../components/commonInput';
import MobileNumberInput, { COUNTRIES, CountryEntry } from '../components/mobileNumberInput';
import EmailComponent from '../components/emailInput';
import ConfirmDialog from '../components/confirmDialog';
import SuccessDialog from '../components/successDialog';
import { getExchangeRate, listTaxGroups, listTaxRates, createTaxRate, createTaxGroup, listStates, listCurrencies, enableCurrency, setExchangeRate, previewTax, getOrgProfile } from '../services/billing/accountant.service';
import { TaxPreviewResult } from '../schema/billing/misc';
import { listContacts, createContact } from '../services/billing/contact.service';
import { createDocument, issueDocument, getDocument, listDocuments, reviseDraft } from '../services/billing/document.service';
import { Contact, ContactType, CreateContactPayload } from '../schema/billing/contact';
import { DocType } from '../schema/billing/common';
import { CreateDocumentPayload, DocumentLine } from '../schema/billing/document';
import { TaxGroup } from '../schema/billing/misc';
import { REGEX } from '../utils/regex';

// Per-line tax is stored as a "rate key": the GST percent as a string ('18',
// '12'…), or 'EXEMPT' / 'NONE'. The actual backend tax_group_id (and the
// CGST+SGST vs IGST split) is resolved at submit time from place of supply.
// Fallback only, used when the org's tax groups have not loaded. The real
// rates come from the backend, which knows the regime.
const STD_GST_SLABS = [28, 18, 12, 5];
const pctOf = (t: string) => (t === 'EXEMPT' || t === 'NONE' ? 0 : num(t));
const isTaxed = (t: string) => t !== 'EXEMPT' && t !== 'NONE';
// Home state (intra-state) — defaults to Tamil Nadu (33). A place of supply
// equal to this is intra-state (CGST+SGST); anything else is inter-state (IGST).
/**
 * Fallback home state, used only until the org profile loads. The real value is
 * derived from the org's own GSTIN (digits 1-2 ARE the state code), because
 * hardcoding one tenant's state made every other tenant's intra-state supply
 * look inter-state — CGST+SGST vs IGST, which is a wrong tax, not a wrong label.
 *
 * The server is authoritative either way: it derives place of supply from the
 * GSTIN itself, so this only decides what the picker shows before that answer
 * arrives.
 */
const HOME_STATE_FALLBACK = '33';
const homeStateFrom = (gstin?: string | null): string =>
  gstin && /^\d{2}/.test(gstin) ? gstin.slice(0, 2) : HOME_STATE_FALLBACK;

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});
// Consistent per-name gradient for customer avatars (matches the list screens).
const AV: Array<[string, string]> = [
  ['#f97316', '#db2777'], ['#3b82f6', '#6366f1'], ['#10b981', '#059669'],
  ['#f59e0b', '#ea580c'], ['#8b5cf6', '#6d28d9'], ['#06b6d4', '#0891b2'],
];
const avatarOf = (name: string): [string, string] => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % AV.length;
  return AV[h];
};

/** Premium hover-reactive add-line button. */
const AddLineBtn: React.FC<{ label: string; icon: string; primary?: boolean; onPress: () => void }> = ({ label, icon, primary, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [h, setH] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      style={{
        height: 36,
        paddingHorizontal: 14,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: primary ? colors.accentLine : h ? colors.lineStrong : colors.line,
        backgroundColor: primary ? colors.accentSoft : h ? colors.paper2 : colors.card,
        ...web({
          transition: 'all .14s ease',
          cursor: 'pointer',
          transform: h ? 'translateY(-1px)' : 'translateY(0)',
          boxShadow: h ? (primary ? `0 7px 16px -4px ${colors.accent}66` : '0 3px 8px rgba(16,24,40,.10)') : 'none',
        }),
      }}
      {...(Platform.OS === 'web' ? { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) } : {})}
    >
      <SkIcon name={icon} size={14} color={primary ? colors.accentInk : colors.ink3} strokeWidth={2.4} />
      <Text style={{ fontFamily: primary ? font.sansSemibold : font.sansMedium, fontSize: 12.5, fontWeight: primary ? '600' : '500', color: primary ? colors.accentInk : colors.ink2 }}>{label}</Text>
    </Pressable>
  );
};

interface Line { id: string; kind: 'ITEM' | 'SHIPPING' | 'ADJUSTMENT'; desc: string; hsn: string; qty: string; rate: string; taxGroup: string; placeholder?: boolean; }
const uid = () => Math.random().toString(36).slice(2);
const today = () => new Date().toISOString().slice(0, 10);
const isDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
const num = (s: string) => parseFloat(String(s).replace(/,/g, '')) || 0; // tolerate "4,500"

/**
 * Money in the document's own currency.
 *
 * This took a `currency` argument but only branched on USD — every other
 * currency fell through to the ₹ path with Indian lakh grouping, so an AED or
 * GBP document rendered as rupees. It now delegates to the shared formatter,
 * which reads scale and symbol per currency.
 */
const fmtMoney2 = (n: number, currency: string): string => fmtMoney(n, currency);

/** "2500.0000" → "2500", "2.5000" → "2.5" — the server stores 4 dp. */
const trimZeros = (v: unknown): string => {
  const t = String(v ?? '');
  return t.includes('.') ? t.replace(/\.?0+$/, '') : t;
};

/**
 * What another screen hands a NEW document: a credit note raised from an
 * invoice arrives already pointing at that invoice, its customer and lines.
 */
export interface EditorPrefill {
  linkedDocumentId?: string;
  linkedDocumentNumber?: string;
  contact?: { id: string; name: string } | null;
  placeOfSupply?: string;
  currency?: string;
  lines?: Array<{ description: string; hsn_sac?: string | null; quantity: string; unit_price: string; tax_group_id?: string | null }>;
}

const DocumentEditor: React.FC<{ doc?: DocRow | null; docType?: DocType; prefill?: EditorPrefill; onBack: () => void }> = ({ doc, docType, prefill, onBack }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const { isWide, isMobile, width } = useResponsive();
  // Totals sit beside the form only when the line table still has room for a
  // readable Item column next to them (sidebar + totals + ~880px of lines);
  // narrower, they stack below. At 1366px the side-by-side layout squeezed the
  // item name to ~40px.
  const totalsBeside = width >= 1560;
  const { role } = useSession();

  const dt: DocType = docType ?? (doc?.type as DocType) ?? 'INVOICE';
  const isQuote = dt === 'QUOTE';
  const isInvoice = dt === 'INVOICE';
  const isExpense = dt === 'EXPENSE';
  const isCreditNote = dt === 'CREDIT_NOTE';
  // Purchase-side documents bill FROM a vendor (money out) rather than TO a customer.
  const isPurchase = dt === 'BILL' || dt === 'VENDOR_CREDIT' || dt === 'EXPENSE';
  const partyType: ContactType = isPurchase ? 'VENDOR' : 'CUSTOMER';
  const partyWord = isPurchase ? 'vendor' : 'customer';
  const partyLabel = isExpense ? 'Paid to' : isPurchase ? 'Bill from' : 'Bill to';
  const DOC_WORD: Record<string, string> = { QUOTE: 'quote', INVOICE: 'invoice', CREDIT_NOTE: 'credit note', BILL: 'bill', VENDOR_CREDIT: 'vendor credit', EXPENSE: 'expense' };
  const docWord = DOC_WORD[dt] ?? 'document';
  const titleWord = docWord;
  const Cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const dateLabel = isQuote ? 'Quote date' : isPurchase || isCreditNote ? `${Cap(docWord)} date` : 'Invoice date';
  const dueLabel = isQuote ? 'Valid until' : 'Due date';

  const [contact, setContact] = useState<{ id: string; name: string } | null>(prefill?.contact ?? null);
  // A credit note must name the invoice it credits (the server refuses one
  // that does not). Pre-filled when raised from the invoice itself.
  const [linked, setLinked] = useState<{ id: string; number: string } | null>(
    prefill?.linkedDocumentId ? { id: prefill.linkedDocumentId, number: prefill.linkedDocumentNumber || '' } : null,
  );
  const [linkOptions, setLinkOptions] = useState<string[][]>([]);
  const [invDate, setInvDate] = useState(doc?.date && isDate(doc.date) ? doc.date : today());
  const [dueDate, setDueDate] = useState(doc?.due && doc.due !== '—' && isDate(doc.due) ? doc.due : '');
  const [homeState, setHomeState] = useState(HOME_STATE_FALLBACK);
  // The currency the org books in. Drives the FX comparison line; INR only
  // until the profile answers, since a UAE tenant books in AED.
  const [baseCurrency, setBaseCurrency] = useState('INR');
  const [pos, setPos] = useState(HOME_STATE_FALLBACK);
  const [currency, setCurrency] = useState('INR');
  const [posOptions, setPosOptions] = useState<string[][]>([]);
  const [curOptions, setCurOptions] = useState<string[][]>([['INR', 'INR · ₹']]);
  const [discountPct, setDiscountPct] = useState('0');
  const [discountPreTax, setDiscountPreTax] = useState(true);
  const [fxRate, setFxRate] = useState<number | null>(null);
  const [curAddOpen, setCurAddOpen] = useState(false);
  const [taxGroups, setTaxGroups] = useState<TaxGroup[]>([]);
  const [saving, setSaving] = useState(false);
  const [addCustOpen, setAddCustOpen] = useState(false);
  const [addPrefill, setAddPrefill] = useState('');
  const [lines, setLines] = useState<Line[]>([
    { id: uid(), kind: 'ITEM', desc: '', hsn: '', qty: '1', rate: '0', taxGroup: '18' },
  ]);
  const [taxAddLineId, setTaxAddLineId] = useState<string | null>(null);

  // An existing saved draft (opened from the list) — vs a brand-new document.
  const existingId = doc?.id ?? null;
  // The draft this editor writes to. Set on open, or after the first save —
  // so a retry after a failed issue revises THIS draft instead of making a
  // second one.
  const [savedId, setSavedId] = useState<string | null>(existingId);

  // Backend-driven option lists: tax groups, place-of-supply states, currencies.
  useEffect(() => {
    let alive = true;
    listTaxGroups().then((g) => { if (alive) setTaxGroups(Array.isArray(g) ? g : []); }).catch(() => {});
    // The org's own GSTIN carries its state in digits 1-2, so intra vs
    // inter-state follows the tenant rather than a constant. A new document
    // also defaults its place of supply to home, which is the common case.
    getOrgProfile()
      .then((p) => {
        if (!alive) return;
        const home = homeStateFrom(p?.gstin);
        setHomeState(home);
        if (!existingId && !prefill?.placeOfSupply) setPos(home);
        if (p?.base_currency) { setBaseCurrency(p.base_currency); if (!existingId && !prefill?.currency) setCurrency(p.base_currency); }
      })
      .catch(() => {});
    listStates()
      .then((s) => { if (alive && Array.isArray(s) && s.length) setPosOptions(s.map((x) => [x.code, `${x.code} · ${x.name}`])); })
      .catch(() => {});
    listCurrencies()
      .then((cs) => {
        if (!alive || !Array.isArray(cs)) return;
        const enabled = cs.filter((c: any) => c.is_enabled !== false).map((c: any) => { const cc = c.currency_code ?? c.code; return [cc, cc] as string[]; });
        if (enabled.length) setCurOptions(enabled);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  /** Reverse-map a backend tax_group_id → the editor's rate key ('18', 'EXEMPT'…). */
  const idToTaxGroup = (id?: string | null): string => {
    if (!id) return 'NONE';
    const g = taxGroups.find((x) => x.id === id);
    if (!g) return '18';
    if (g.is_exempt || /exempt|nil/i.test(g.name)) return 'EXEMPT';
    const m = g.name.match(/(\d+(?:\.\d+)?)\s*%?/);
    return m ? m[1] : 'NONE';
  };

  /**
   * Per-line tax options, from the BACKEND tax groups.
   *
   * The group's own name is the label ("GST 18%", "VAT 5%"), so a UAE org sees
   * VAT without the frontend knowing which regime applies. The India slabs are
   * only a fallback for an org whose groups have not loaded or been seeded yet
   * — offering 28/18/12/5 to a VAT org would be offering rates that do not
   * exist there.
   */
  const taxRateOptions = useMemo<string[][]>(() => {
    const byPct = new Map<number, string>();
    taxGroups.forEach((g) => {
      if (g.is_exempt) return;
      const m = g.name.match(/(\d+(?:\.\d+)?)\s*%/);
      if (!m) return;
      // India seeds "GST 18%" and "IGST 18%" for one rate; the server picks
      // the split from place of supply, so label the rate by the neutral name.
      const pct = Number(m[1]);
      const cur = byPct.get(pct);
      if (!cur || (/^IGST\b/i.test(cur) && !/^IGST\b/i.test(g.name))) byPct.set(pct, g.name);
    });
    if (byPct.size === 0) STD_GST_SLABS.forEach((p) => byPct.set(p, `GST ${p}%`));
    const opts = [...byPct.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([p, label]) => [String(p), label]);
    opts.push(['EXEMPT', 'Exempt'], ['NONE', 'No tax']);
    return opts;
  }, [taxGroups]);

  // Load an existing draft's real data so the editor reflects it (and Issue
  // can flip THIS document in place). Re-runs once tax groups arrive so the
  // per-line GST selection maps correctly.
  useEffect(() => {
    if (!existingId) return;
    let alive = true;
    getDocument(existingId)
      .then((full: any) => {
        if (!alive || !full) return;
        if (full.contact_id) setContact({ id: full.contact_id, name: full.contact_name || full.contact_id });
        if (full.doc_date) setInvDate(full.doc_date);
        if (full.due_date || full.valid_until) setDueDate(full.due_date || full.valid_until);
        if (full.currency) setCurrency(full.currency);
        if (full.place_of_supply) setPos(String(full.place_of_supply));
        const ls = Array.isArray(full.lines) ? full.lines : [];
        if (ls.length) {
          setLines(
            ls.map((l: any) => ({
              id: uid(),
              kind: 'ITEM' as const,
              desc: l.description || '',
              hsn: l.hsn_sac || '',
              qty: trimZeros(l.quantity ?? '1'),
              rate: trimZeros(l.unit_price ?? '0'),
              taxGroup: idToTaxGroup(l.tax_group_id),
            })),
          );
        }
        if (full.linked_document_id) setLinked((cur) => cur ?? { id: full.linked_document_id, number: '' });
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingId, taxGroups.length]);

  // A new document handed a prefill: fill it once tax groups are known, so each
  // line maps to the right rate.
  const [prefillDone, setPrefillDone] = useState(false);
  useEffect(() => {
    if (existingId || !prefill || prefillDone || taxGroups.length === 0) return;
    if (prefill.placeOfSupply) setPos(String(prefill.placeOfSupply));
    if (prefill.currency) setCurrency(prefill.currency);
    if (prefill.lines?.length) {
      setLines(
        prefill.lines.map((l) => ({
          id: uid(),
          kind: 'ITEM' as const,
          desc: l.description || '',
          hsn: l.hsn_sac || '',
          qty: trimZeros(l.quantity),
          rate: trimZeros(l.unit_price),
          taxGroup: idToTaxGroup(l.tax_group_id),
        })),
      );
    }
    setPrefillDone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxGroups.length]);

  // Credit note: the customer's issued invoices it can be raised against.
  useEffect(() => {
    if (!isCreditNote || !contact?.id) { setLinkOptions([]); return; }
    let alive = true;
    listDocuments({ type: 'INVOICE' })
      .then((rows: any[]) => {
        if (!alive) return;
        setLinkOptions(
          (Array.isArray(rows) ? rows : [])
            .filter((d) => d.contact_id === contact.id && ['ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE'].includes(d.status))
            .map((d) => [d.id, `${d.doc_number} · ${fmtMoney(Number(d.grand_total) || 0, d.currency || 'INR')}`]),
        );
      })
      .catch(() => { if (alive) setLinkOptions([]); });
    return () => { alive = false; };
  }, [isCreditNote, contact?.id]);

  // Live FX for the INR equivalent shown when billing in a non-base currency.
  useEffect(() => {
    if (currency === baseCurrency) { setFxRate(null); return; }
    let alive = true;
    getExchangeRate(currency, baseCurrency)
      .then((r: any) => {
        const rate = typeof r === 'number' ? r : Number(r?.rate ?? r?.value);
        setFxRate(alive && Number.isFinite(rate) && rate > 0 ? rate : null);
      })
      .catch(() => { if (alive) setFxRate(null); });
    return () => { alive = false; };
  }, [currency, baseCurrency]);

  const inter = pos !== homeState;

  /**
   * Tax is computed by the SERVER, never here (SKBILLING-GLOBAL-CR-001).
   *
   * This used to be a full India-GST engine in the browser: CGST/SGST splitting,
   * IGST, slab lookup and rounding, all keyed off a hardcoded home state. It
   * duplicated taxEngine.ts and could silently disagree with the issued
   * document. POST /v1/taxes/preview resolves jurisdiction, region-pack config
   * and place of supply on the same inputs `issue` uses, so the number on screen
   * is the number on the invoice — and a UAE org sees VAT without the frontend
   * knowing a thing about which regime applies.
   *
   * Subtotal and discount stay local: they are plain arithmetic on what the user
   * is typing, they must not lag behind a keystroke, and they carry no
   * jurisdiction. Only tax crosses the wire.
   */
  const [taxResult, setTaxResult] = useState<TaxPreviewResult | null>(null);
  const [taxPending, setTaxPending] = useState(false);

  /** Map the editor's rate key to a backend tax_group_id (intra→GST, inter→IGST). */
  const taxGroupId = useCallback(
    (taxGroup: string): string | undefined => {
      if (taxGroup === 'NONE') return undefined;
      if (taxGroup === 'EXEMPT') return taxGroups.find((g) => /exempt/i.test(g.name) || g.is_exempt)?.id;

      // Match on the RATE, not a constructed name. This built `GST 18%` or
      // `IGST 18%` and compared it exactly, so a UAE org whose groups are named
      // `VAT 5%` matched nothing and the line was sent with NO tax group at
      // all. The rate is the thing both regimes share; the server resolves the
      // intra/inter split from place of supply, so the frontend does not need
      // to guess which name the group carries.
      const want = pctOf(taxGroup);
      const rateOf = (n: string): number | null => {
        const m = n.match(/(\d+(?:\.\d+)?)\s*%/);
        return m ? Number(m[1]) : null;
      };
      const candidates = taxGroups.filter((g) => !g.is_exempt && rateOf(g.name) === want);
      if (candidates.length === 0) return undefined;
      // Where a regime names both directions (India seeds GST and IGST at the
      // same rate), prefer the one matching this document's direction.
      const directional = candidates.find((g) =>
        inter ? /^IGST\b/i.test(g.name) : !/^IGST\b/i.test(g.name),
      );
      return (directional ?? candidates[0])?.id;
    },
    [taxGroups, inter],
  );

  const local = useMemo(() => {
    const subtotal = lines.reduce((a, l) => a + num(l.qty) * num(l.rate), 0);
    const disc = (subtotal * num(discountPct)) / 100;
    const preFactor = discountPreTax && subtotal > 0 ? (subtotal - disc) / subtotal : 1;
    const taxLines = lines
      .filter((l) => (l.kind === 'ITEM' || l.kind === 'SHIPPING') && num(l.qty) * num(l.rate) > 0)
      .map((l) => ({
        amount: (num(l.qty) * num(l.rate) * preFactor).toFixed(2),
        tax_group_id: taxGroupId(l.taxGroup),
      }));
    return { subtotal, disc, taxLines };
  }, [lines, discountPct, discountPreTax, taxGroups, inter]);

  // Debounced so typing a rate does not fire a request per keystroke.
  useEffect(() => {
    if (local.taxLines.length === 0) { setTaxResult(null); setTaxPending(false); return; }
    let alive = true;
    setTaxPending(true);
    const t = setTimeout(() => {
      previewTax({ place_of_supply: pos, lines: local.taxLines })
        .then((r) => { if (alive) { setTaxResult(r); setTaxPending(false); } })
        // Leave the last good figures on screen rather than flashing zeros; the
        // server is authoritative again at issue time regardless.
        .catch(() => { if (alive) setTaxPending(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [local.taxLines, pos]);

  const tot = useMemo(() => {
    const taxRows = (taxResult?.taxes_by_component ?? []).map((c) => ({
      label: `${c.name} @${c.percent}%`,
      amount: num(String(c.amount)),
    }));
    const grand = taxResult ? num(String(taxResult.grand_total)) : local.subtotal - local.disc;
    return {
      subtotal: local.subtotal,
      disc: local.disc,
      taxRows,
      grand,
      rounding: taxResult ? num(String(taxResult.rounding_delta)) : 0,
      // The server decides Bill of Supply vs Tax Invoice; until it answers, fall
      // back to "no taxable line" rather than asserting a variant of our own.
      isBOS: taxResult ? taxResult.print_variant === 'BILL_OF_SUPPLY' : local.taxLines.length === 0,
      pending: taxPending,
    };
  }, [taxResult, local, taxPending]);

  const itemLines = lines.filter((l) => l.kind === 'ITEM' && (l.desc.trim() || num(l.rate) > 0));
  const requiresParty = !isExpense; // expenses can be recorded without naming a vendor
  const unresolved = requiresParty && !contact ? 1 : 0;
  const canIssue = unresolved === 0 && itemLines.length > 0 && role !== 'VIEWER';
  const setLine = (id: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const addLine = (kind: Line['kind']) => setLines((ls) => [...ls, { id: uid(), kind, desc: kind === 'SHIPPING' ? 'Shipping charge' : kind === 'ADJUSTMENT' ? 'Adjustment' : '', hsn: '', qty: '1', rate: '0', taxGroup: 'NONE' }]);
  const removeLine = (id: string) => setLines((ls) => ls.filter((l) => l.id !== id));
  const [issueOpen, setIssueOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [backHover, setBackHover] = useState(false);
  const [saveHover, setSaveHover] = useState(false);
  const [issueHover, setIssueHover] = useState(false);
  const [lineToDelete, setLineToDelete] = useState<Line | null>(null);
  const [lineDeletedOpen, setLineDeletedOpen] = useState(false);

  /** Build the CreateDocumentPayload from editor state. */
  const buildPayload = (): CreateDocumentPayload => {
    const dp = num(discountPct);
    const itemPayload: DocumentLine[] = itemLines.map((l) => ({
      description: l.desc.trim() || 'Item',
      quantity: String(num(l.qty) || 1),
      unit_price: String(num(l.rate)),
      hsn_sac: l.hsn.trim() || undefined,
      tax_group_id: taxGroupId(l.taxGroup),
      ...(dp > 0 ? { discount_percent: String(dp) } : {}),
    }));
    const sumKind = (k: Line['kind']) => lines.filter((l) => l.kind === k).reduce((s, l) => s + num(l.qty) * num(l.rate), 0);
    return {
      doc_type: dt,
      contact_id: contact?.id,
      contact_name: contact?.name,
      ...(isDate(invDate) ? { doc_date: invDate } : {}),
      ...(isDate(dueDate) ? { due_date: dueDate } : {}),
      currency,
      place_of_supply: pos.slice(0, 2), // pos is the 2-digit GST state code
      ...(isCreditNote && linked ? { linked_document_id: linked.id } : {}),

      discount_is_pre_tax: discountPreTax,
      shipping_total: sumKind('SHIPPING').toFixed(4),
      adjustment_total: sumKind('ADJUSTMENT').toFixed(4),
      lines: itemPayload,
    };
  };

  const validate = (forIssue: boolean): boolean => {
    if (role === 'VIEWER') { showToast('warn', 'Read-only', 'Switch to ADMIN/EDITOR to save'); return false; }
    if (itemLines.length === 0) { showToast('warn', 'Add a line item', `A ${docWord} needs at least one item with a description or amount`); return false; }
    if (isCreditNote && !linked) { showToast('warn', 'Choose the invoice', 'A credit note must name the invoice it credits.'); return false; }
    if (forIssue && requiresParty && !contact) { showToast('warn', `Select a ${partyWord}`, isPurchase ? `Choose the ${partyWord} before issuing` : 'Choose who to bill before issuing'); return false; }
    if (isDate(invDate) && isDate(dueDate) && new Date(dueDate).getTime() < new Date(invDate).getTime()) {
      showToast('warn', `${dueLabel} can't be before the ${dateLabel.toLowerCase()}`, 'Fix the dates before continuing.');
      return false;
    }
    return true;
  };

  // Save draft is gated by a confirm dialog (parity with Issue's confirmation).
  const askSaveDraft = () => {
    if (saving || !validate(false)) return;
    setSaveConfirmOpen(true);
  };
  /** Write the editor's state to its draft: revise it in place, or create it once. */
  const persist = async (): Promise<string> => {
    if (savedId) {
      await reviseDraft(savedId, buildPayload());
      return savedId;
    }
    const created: any = await createDocument(buildPayload());
    setSavedId(created.id);
    return created.id;
  };

  const doSaveDraft = async () => {
    setSaveConfirmOpen(false);
    if (saving) return;
    setSaving(true);
    try {
      const revising = !!savedId;
      await persist();
      if (revising) showToast('success', 'Draft saved', 'Your changes are saved.');
      onBack();
    } catch {
      setSaving(false); // error toast shown by the service layer
    }
  };

  const onIssue = () => {
    if (!validate(true)) return;
    setIssueOpen(true);
  };

  const confirmIssue = async () => {
    setIssueOpen(false);
    if (saving) return;
    setSaving(true);
    try {
      // Save what is ON SCREEN first (revise in place, or create once), then
      // issue that same draft. A failed issue keeps savedId, so the retry
      // revises and issues this draft rather than creating another.
      const id = await persist();
      await issueDocument(id);
      showToast('success', `${Cap(docWord)} issued`, isQuote ? 'Number assigned — ready to send to the customer' : 'Number assigned, ledger posted, now immutable');
      onBack();
    } catch {
      setSaving(false);
    }
  };

  // Enable a new currency in the org (+ optional ₹ rate), refresh the list, select it.
  const refreshCurrencies = async () => {
    const cs = await listCurrencies();
    const enabled = (Array.isArray(cs) ? cs : []).filter((c: any) => c.is_enabled !== false).map((c: any) => { const cc = c.currency_code ?? c.code; return [cc, cc] as string[]; });
    if (enabled.length) setCurOptions(enabled);
  };
  const addCurrency = async (code: string, rate: string) => {
    const c = code.toUpperCase().slice(0, 3);
    if (!/^[A-Z]{3}$/.test(c)) { showToast('warn', 'Invalid code', 'Use a 3-letter currency code, e.g. EUR'); return; }
    try {
      await enableCurrency(c);
      if (rate.trim() && Number(rate) > 0) await setExchangeRate(c, 'INR', rate.trim());
      await refreshCurrencies();
      setCurrency(c);
      setCurAddOpen(false);
    } catch {
      /* error toast shown by the service */
    }
  };

  // Add a new GST tax slab: creates the underlying CGST/SGST + IGST rates (or
  // reuses existing ones) and the matching "GST x%" / "IGST x%" groups, then
  // refreshes the list and selects it on the line that opened the dialog.
  const addTax = async (pctStr: string) => {
    const p = num(pctStr);
    if (!(p > 0) || p > 100) { showToast('warn', 'Invalid rate', 'Enter a tax percent between 0 and 100'); return; }
    const lineId = taxAddLineId;
    try {
      const groups = await listTaxGroups();
      const hasGst = groups.some((g) => g.name === `GST ${p}%`);
      const hasIgst = groups.some((g) => g.name === `IGST ${p}%`);
      if (!hasGst || !hasIgst) {
        const rates = await listTaxRates();
        const half = p / 2;
        const findOrCreate = async (kind: string, pct: number, name: string): Promise<string> => {
          const ex = rates.find((r: any) => r.tax_kind === kind && Number(r.rate_percent) === pct);
          if (ex) return ex.id;
          const created = await createTaxRate({ name, rate_percent: String(pct), tax_kind: kind });
          return (created as any).id;
        };
        const cgst = await findOrCreate('CGST', half, `CGST ${half}%`);
        const sgst = await findOrCreate('SGST', half, `SGST ${half}%`);
        const igst = await findOrCreate('IGST', p, `IGST ${p}%`);
        if (!hasGst) await createTaxGroup({ name: `GST ${p}%`, component_rate_ids: [cgst, sgst] });
        if (!hasIgst) await createTaxGroup({ name: `IGST ${p}%`, component_rate_ids: [igst] });
      }
      const refreshed = await listTaxGroups();
      setTaxGroups(Array.isArray(refreshed) ? refreshed : []);
      if (lineId) setLine(lineId, { taxGroup: String(p) });
      setTaxAddLineId(null);
    } catch {
      /* error toast shown by the service */
    }
  };

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14 } as const;
  const fm = (n: number) => fmtMoney2(n, currency);
  const fieldLabel = { fontFamily: font.sans, fontSize: 12, color: colors.ink2, marginBottom: 4, fontWeight: '500' as const };

  const CW = { item: 240, hsn: 100, qty: 66, rate: 110, tax: 132, amount: 120, rm: 40 };
  const GRID_GAP = 12;
  const gridMin = Object.values(CW).reduce((a, b) => a + b, 0) + GRID_GAP * 6;
  // Shared premium "field" look for editable line cells.
  const cellInput = { height: 34, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 8, paddingHorizontal: 10, fontFamily: font.mono, fontSize: 13, color: colors.ink, ...web({ outlineStyle: 'none' }) } as const;
  const hCell = { fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600' as const, color: colors.ink4, textTransform: 'uppercase' as const, letterSpacing: 0.6 };

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 13, marginBottom: 16, flexWrap: 'wrap' }}>
        <Pressable
          onPress={onBack}
          style={{ width: 36, height: 36, borderWidth: 1, borderColor: backHover ? colors.lineStrong : colors.line, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: backHover ? colors.paper2 : colors.card, ...theme.shadow('sm'), ...web({ transition: 'all .14s ease', cursor: 'pointer' }) }}
          {...(isWeb ? { onMouseEnter: () => setBackHover(true), onMouseLeave: () => setBackHover(false) } : {})}
        >
          <SkIcon name="chevLeft" size={17} color={colors.ink2} strokeWidth={2.2} />
        </Pressable>
        <View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
            <Text style={{ fontFamily: font.sansBold, fontSize: 19, fontWeight: '700', color: colors.ink, letterSpacing: -0.3 }}>{doc?.ai ? `${Cap(docWord)} — AI draft` : existingId ? `${Cap(docWord)} draft` : `New ${titleWord}`}</Text>
            <View style={{ paddingHorizontal: 8, height: 20, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '700', letterSpacing: 0.6, color: colors.ink3 }}>DRAFT</Text>
            </View>
            {isInvoice && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: tot.isBOS ? colors.neutralSoft : colors.accentSoft, borderWidth: 1, borderColor: tot.isBOS ? colors.neutralLine : colors.accentLine }}>
                <SkIcon name="checkCircle" size={12} color={tot.isBOS ? colors.neutral : colors.accentInk} strokeWidth={2} />
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 11, fontWeight: '600', color: tot.isBOS ? colors.neutral : colors.accentInk }}>{tot.isBOS ? 'Bill of Supply' : 'Tax Invoice'}</Text>
              </View>
            )}
          </View>
          <Text style={{ fontFamily: font.sans, fontSize: 11.5, color: colors.ink4, marginTop: 3 }}>Number assigned on issue</Text>
        </View>
        <View style={{ flex: 1 }} />
        {(
          <Pressable
            onPress={askSaveDraft}
            disabled={saving}
            style={{ height: 38, paddingHorizontal: 16, borderWidth: 1, borderColor: saveHover ? colors.lineStrong : colors.line, borderRadius: 10, flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: saveHover ? colors.paper2 : colors.card, ...theme.shadow('sm'), ...web({ transition: 'all .14s ease', cursor: 'pointer' }) }}
            {...(isWeb ? { onMouseEnter: () => setSaveHover(true), onMouseLeave: () => setSaveHover(false) } : {})}
          >
            <SkIcon name="download" size={14} color={colors.ink2} strokeWidth={2} />
            <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>{saving ? 'Saving…' : 'Save draft'}</Text>
          </Pressable>
        )}
        <Pressable
          onPress={onIssue}
          disabled={saving}
          style={{
            height: 38, paddingHorizontal: 18, borderRadius: 10, flexDirection: 'row', alignItems: 'center', gap: 7,
            borderWidth: 1, backgroundColor: canIssue ? colors.accent : colors.paper3, borderColor: canIssue ? colors.transparent : colors.line,
            ...(canIssue
              ? web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: issueHover ? `0 8px 18px -4px ${colors.accent}aa` : `0 5px 12px -3px ${colors.accent}77`, transform: issueHover ? 'translateY(-1px)' : 'translateY(0)', transition: 'transform .14s ease, box-shadow .14s ease', cursor: 'pointer' })
              : web({ cursor: 'not-allowed' })),
          }}
          {...(isWeb ? { onMouseEnter: () => setIssueHover(true), onMouseLeave: () => setIssueHover(false) } : {})}
        >
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: canIssue ? '#fff' : colors.ink4 }}>Issue</Text>
          <SkIcon name="arrowRight" size={15} color={canIssue ? '#fff' : colors.ink4} strokeWidth={2.2} />
          {unresolved > 0 && (
            <View style={{ minWidth: 18, height: 18, paddingHorizontal: 5, borderRadius: 999, backgroundColor: canIssue ? 'rgba(255,255,255,.25)' : colors.line, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontSize: 11, fontWeight: '700', color: canIssue ? '#fff' : colors.ink4 }}>{unresolved}</Text>
            </View>
          )}
        </Pressable>
      </View>

      <View style={{ flexDirection: totalsBeside ? 'row' : 'column', gap: 16 }}>
        {/* Left: form + lines */}
        <View style={{ flex: totalsBeside ? 1 : undefined, gap: 14 }}>
          {/* header form */}
          <View style={[card, { zIndex: 10, ...web({ position: 'relative' }) }, theme.shadow('sm')]}>
            {/* card header */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14, paddingHorizontal: 16, borderBottomWidth: 1, borderColor: colors.line }}>
              <View style={{ width: 30, height: 30, borderRadius: 9, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
                <SkIcon name="building" size={16} color={colors.accentInk} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>{partyLabel} &amp; details</Text>
            </View>
            <View style={{ padding: 16, gap: 14 }}>
              <View style={{ zIndex: 20 }}>
                <Text style={fieldLabel}>{partyLabel}</Text>
                <ContactSearchField
                  type={partyType}
                  word={partyWord}
                  contact={contact}
                  optional={isExpense}
                  onSelect={(c) => { setContact({ id: c.id, name: c.name }); if (isCreditNote && c.id !== contact?.id) setLinked(null); }}
                  onClear={() => { setContact(null); if (isCreditNote) setLinked(null); }}
                  onAddNew={(prefill) => { setAddPrefill(prefill); setAddCustOpen(true); }}
                />
              </View>
              {isCreditNote && (
                <View style={{ zIndex: 15 }}>
                  <Text style={fieldLabel}>Against invoice</Text>
                  {!contact ? (
                    <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4 }}>Choose the customer first.</Text>
                  ) : linkOptions.length === 0 && !linked ? (
                    <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.warn }}>This customer has no issued invoice to credit.</Text>
                  ) : (
                    <DropdownSelect
                      value={linked?.id ?? ''}
                      options={linked && !linkOptions.some(([id]) => id === linked.id) ? [[linked.id, linked.number || 'Linked invoice'], ...linkOptions] : linkOptions}
                      onChange={(id) => {
                        const opt = linkOptions.find(([v]) => v === id);
                        setLinked({ id, number: opt ? opt[1].split(' · ')[0] : '' });
                      }}
                      placeholder="Select the invoice"
                    />
                  )}
                </View>
              )}
              <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
                <View style={{ flex: 1 }}><Text style={fieldLabel}>{dateLabel}</Text><DateField value={invDate} onChange={setInvDate} /></View>
                {!isCreditNote && (
                  <View style={{ flex: 1 }}><Text style={fieldLabel}>{dueLabel}</Text><DateField value={dueDate} onChange={setDueDate} min={invDate} minMessage={`${dueLabel} can't be before the ${dateLabel.toLowerCase()}`} /></View>
                )}
              </View>
              <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
                <View style={{ flex: 1 }}><Text style={fieldLabel}>Place of supply</Text><DropdownSelect value={pos} options={posOptions} onChange={setPos} /></View>
                <View style={{ flex: 1 }}><Text style={fieldLabel}>Currency</Text><DropdownSelect value={currency} options={curOptions} onChange={setCurrency} customLabel="Other currency…" onCustom={() => setCurAddOpen(true)} /></View>
              </View>
            </View>
          </View>

          {/* line grid */}
          <View style={[card, { overflow: 'hidden' }, theme.shadow('sm')]}>
            {/* card header */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14, paddingHorizontal: 16, borderBottomWidth: 1, borderColor: colors.line }}>
              <View style={{ width: 30, height: 30, borderRadius: 9, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
                <SkIcon name="fileText" size={16} color={colors.accentInk} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>Line items</Text>
              <View style={{ flex: 1 }} />
              <View style={{ paddingHorizontal: 9, height: 22, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ fontFamily: font.monoMedium, fontSize: 11, color: colors.ink3 }}>{lines.filter((l) => l.kind === 'ITEM').length} item{lines.filter((l) => l.kind === 'ITEM').length === 1 ? '' : 's'}</Text>
              </View>
            </View>
            <ScrollView horizontal={isMobile} showsHorizontalScrollIndicator={false}>
              <View style={{ width: isMobile ? gridMin : '100%' }}>
                {/* header */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: GRID_GAP, backgroundColor: colors.paper2, borderBottomWidth: 1, borderColor: colors.line, paddingVertical: 11, paddingHorizontal: 14, ...web({ backgroundImage: `linear-gradient(180deg, ${colors.paper2}, ${colors.paper})` }) }}>
                  <Text style={[hCell, { flex: isMobile ? undefined : 1, width: isMobile ? CW.item : undefined, paddingLeft: 10 }]}>Item / Description</Text>
                  <Text style={[hCell, { width: CW.hsn, paddingLeft: 10 }]}>HSN / SAC</Text>
                  <Text style={[hCell, { width: CW.qty, textAlign: 'right', paddingRight: 10 }]}>Qty</Text>
                  <Text style={[hCell, { width: CW.rate, textAlign: 'right', paddingRight: 10 }]}>Rate</Text>
                  <Text style={[hCell, { width: CW.tax, paddingLeft: 11 }]}>Tax</Text>
                  <Text style={[hCell, { width: CW.amount, textAlign: 'right' }]}>Amount</Text>
                  <View style={{ width: CW.rm }} />
                </View>
                {lines.map((l) => {
                  const amt = num(l.qty) * num(l.rate);
                  const isCharge = l.kind !== 'ITEM';
                  return (
                    <View key={l.id} style={{ flexDirection: 'row', alignItems: 'center', gap: GRID_GAP, borderBottomWidth: 1, borderColor: colors.line, paddingVertical: 10, paddingHorizontal: 14, backgroundColor: isCharge ? colors.paper : colors.card }}>
                      <View style={{ width: isMobile ? CW.item : undefined, flex: isMobile ? undefined : 1 }}>
                        {isCharge ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <View style={{ backgroundColor: colors.neutralSoft, borderWidth: 1, borderColor: colors.neutralLine, borderRadius: 5, paddingHorizontal: 7, paddingVertical: 3 }}>
                              <Text style={{ fontSize: 9, fontWeight: '700', color: colors.neutral, letterSpacing: 0.4 }}>{l.kind}</Text>
                            </View>
                            <TextInput value={l.desc} onChangeText={(t) => setLine(l.id, { desc: t })} style={[cellInput, { flex: 1, fontFamily: font.sans }]} />
                          </View>
                        ) : (
                          <TextInput value={l.desc} onChangeText={(t) => setLine(l.id, { desc: t })} placeholder="Item name or description" placeholderTextColor={colors.ink4} style={[cellInput, { fontFamily: font.sans, fontWeight: '500' }]} />
                        )}
                      </View>
                      {isCharge ? (
                        <View style={{ width: CW.hsn, alignItems: 'center' }}><Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4 }}>—</Text></View>
                      ) : (
                        <TextInput value={l.hsn} onChangeText={(t) => setLine(l.id, { hsn: t })} placeholder="—" placeholderTextColor={colors.ink4} style={[cellInput, { width: CW.hsn, fontSize: 12, color: colors.ink2 }]} />
                      )}
                      <TextInput value={l.qty} onChangeText={(t) => setLine(l.id, { qty: t })} keyboardType="decimal-pad" style={[cellInput, { width: CW.qty, textAlign: 'right' }]} />
                      <TextInput value={l.rate} onChangeText={(t) => setLine(l.id, { rate: t })} keyboardType="decimal-pad" style={[cellInput, { width: CW.rate, textAlign: 'right' }]} />
                      <View style={{ width: CW.tax }}>
                        {l.kind !== 'ADJUSTMENT' ? (
                          <DropdownSelect
                            value={l.taxGroup}
                            options={taxRateOptions}
                            onChange={(v) => setLine(l.id, { taxGroup: v })}
                            customLabel="Add tax…"
                            onCustom={() => setTaxAddLineId(l.id)}
                          />
                        ) : (
                          <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4, textAlign: 'center' }}>—</Text>
                        )}
                      </View>
                      <Text style={{ width: CW.amount, textAlign: 'right', fontFamily: font.monoSemibold, fontSize: 14, fontWeight: '600', color: colors.ink, letterSpacing: -0.2 }}>{fm(amt)}</Text>
                      <Pressable onPress={() => setLineToDelete(l)} hitSlop={6} style={({ pressed }) => ({ width: CW.rm, height: 30, alignItems: 'center', justifyContent: 'center', borderRadius: 7, backgroundColor: pressed ? colors.dangerSoft : 'transparent', ...web({ cursor: 'pointer' }) })}>
                        {({ pressed }) => <SkIcon name="trash" size={15} color={pressed ? colors.danger : colors.ink4} strokeWidth={2} />}
                      </Pressable>
                    </View>
                  );
                })}
                <View style={{ flexDirection: 'row', gap: 8, padding: 14 }}>
                  <AddLineBtn label="Add line" icon="plus" primary onPress={() => addLine('ITEM')} />
                  <AddLineBtn label="Shipping" icon="cart" onPress={() => addLine('SHIPPING')} />
                  <AddLineBtn label="Adjustment" icon="sliders" onPress={() => addLine('ADJUSTMENT')} />
                </View>
              </View>
            </ScrollView>
          </View>
        </View>

        {/* Right: totals rail */}
        <View style={{ width: totalsBeside ? 332 : undefined, marginTop: totalsBeside ? 16 : 0 }}>
          <View style={[card, { padding: 0, borderRadius: 14, overflow: 'hidden' }, theme.shadow('sm')]}>
            {/* header */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14, paddingHorizontal: 16, borderBottomWidth: 1, borderColor: colors.line }}>
              <View style={{ width: 30, height: 30, borderRadius: 9, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
                <SkIcon name="calculator" size={16} color={colors.accentInk} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>Totals</Text>
            </View>

            {/* body rows */}
            <View style={{ padding: 16, gap: 12 }}>
              <Row label="Subtotal" value={fm(tot.subtotal)} colors={colors} font={font} />
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                  <Text style={{ fontFamily: font.sans, fontSize: 12.5, color: colors.ink3 }}>Discount</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', height: 28, borderWidth: 1, borderColor: colors.line, borderRadius: 7, backgroundColor: colors.paper, paddingRight: 8 }}>
                    <TextInput value={discountPct} onChangeText={setDiscountPct} keyboardType="decimal-pad" style={{ width: 40, height: 26, textAlign: 'right', fontFamily: font.mono, fontSize: 12.5, color: colors.ink, paddingHorizontal: 8, ...web({ outlineStyle: 'none' }) }} />
                    <Text style={{ color: colors.ink4, fontSize: 12, fontFamily: font.mono }}>%</Text>
                  </View>
                </View>
                <Text style={{ fontFamily: font.monoSemibold, fontSize: 12.5, fontWeight: '600', color: tot.disc > 0 ? colors.warn : colors.ink4 }}>{tot.disc > 0 ? '−' + fm(tot.disc) : fm(0)}</Text>
              </View>
              <Pressable onPress={() => setDiscountPreTax((p) => !p)} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start', paddingHorizontal: 9, height: 24, borderRadius: 999, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, ...web({ cursor: 'pointer' }) }}>
                <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.accent }} />
                <Text style={{ fontFamily: font.sansMedium, fontSize: 11, fontWeight: '500', color: colors.accentInk }}>{discountPreTax ? 'Applied before tax' : 'Applied after tax'}</Text>
              </Pressable>
              {tot.taxRows.map((t) => <Row key={t.label} label={t.label} value={fm(t.amount)} colors={colors} font={font} />)}
              <Row label="Rounding" value={(tot.rounding >= 0 ? '+' : '−') + fm(Math.abs(tot.rounding))} colors={colors} font={font} muted />
            </View>

            {/* grand total band */}
            <View style={{ paddingVertical: 16, paddingHorizontal: 16, borderTopWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>Grand total</Text>
                <Text style={{ fontFamily: font.monoSemibold, fontSize: 22, fontWeight: '700', color: colors.accentInk, letterSpacing: -0.6 }}>{fm(tot.grand)}</Text>
              </View>
              {currency !== 'INR' && (fxRate != null
                ? <Text style={{ fontFamily: font.mono, fontSize: 11, color: colors.ink3, textAlign: 'right', marginTop: 4 }}>≈ {fmtMoney2(tot.grand * fxRate, baseCurrency)} @ {fmtMoney2(fxRate, baseCurrency)}</Text>
                : <Text style={{ fontFamily: font.mono, fontSize: 11, color: colors.ink3, textAlign: 'right', marginTop: 4 }}>FX rate pending…</Text>)}
            </View>

            {/* footnote */}
            <View style={{ paddingVertical: 12, paddingHorizontal: 16, borderTopWidth: 1, borderColor: colors.line }}>
              <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4, lineHeight: 16 }}>Money stored to 4 decimals; shown at locale precision. Totals recompute live as you edit.</Text>
            </View>
          </View>
        </View>
      </View>

      {/* Remove a line — confirm, then success */}
      <ConfirmDialog
        visible={lineToDelete != null}
        title="Remove this line?"
        message={lineToDelete ? `“${lineToDelete.desc.trim() || (lineToDelete.kind === 'ITEM' ? 'This item' : lineToDelete.kind === 'SHIPPING' ? 'Shipping charge' : 'Adjustment')}” will be removed from the ${docWord}.` : ''}
        confirmText="Remove line"
        cancelText="Keep it"
        onConfirm={() => { if (lineToDelete) removeLine(lineToDelete.id); setLineToDelete(null); setLineDeletedOpen(true); }}
        onCancel={() => setLineToDelete(null)}
      />
      <SuccessDialog
        visible={lineDeletedOpen}
        title="Line removed"
        message={`The line was removed from the ${docWord}. Totals updated.`}
        primaryAction={{ label: 'Done', onPress: () => setLineDeletedOpen(false) }}
        onClose={() => setLineDeletedOpen(false)}
      />

      <ConfirmDialog
        visible={saveConfirmOpen}
        title={`Save as draft?`}
        message={`This saves the ${docWord} as a draft — you can keep editing or issue it later. No number is assigned and nothing posts to the ledger yet.`}
        confirmText={`Save draft`}
        cancelText="Keep editing"
        confirmColor={colors.accent}
        busy={saving}
        onConfirm={doSaveDraft}
        onCancel={() => (saving ? null : setSaveConfirmOpen(false))}
      />

      <IssueModal
        visible={issueOpen}
        onClose={() => setIssueOpen(false)}
        onConfirm={confirmIssue}
        variant={isInvoice ? (tot.isBOS ? 'Bill of Supply' : 'Tax Invoice') : Cap(docWord)}
        customer={contact?.name ?? '—'}
        grandTotal={fm(tot.grand)}
        docWord={docWord}
        partyLabel={isPurchase ? 'Vendor' : 'Customer'}
        consequences={
          isQuote
            ? [
                'Assign the next quote number',
                'Lock the quote — no ledger entry is posted until it becomes an invoice',
              ]
            : isCreditNote
            ? [
                `Assign the next credit note number${linked?.number ? ` against ${linked.number}` : ''}`,
                'Post a reversing journal entry (Sales · GST Output · Receivable)',
                'Lock the credit note — it becomes read-only and immutable',
              ]
            : isPurchase
            ? [
                `Assign the next number in the ${docWord} series`,
                'Post a balanced journal entry to the ledger (Expense/Asset · GST Input · Payable)',
                'Lock the document — it becomes read-only and immutable',
              ]
            : undefined
        }
      />
      <AddCustomerModal
        type={partyType}
        visible={addCustOpen}
        prefillName={addPrefill}
        onClose={() => setAddCustOpen(false)}
        onCreated={(c) => {
          setContact({ id: c.id, name: c.name });
          if (c.state_code && /^\d{2}$/.test(c.state_code)) setPos(c.state_code);
          setAddCustOpen(false);
        }}
      />
      <AddCurrencyModal visible={curAddOpen} onClose={() => setCurAddOpen(false)} onAdd={addCurrency} />
      <AddTaxModal visible={taxAddLineId != null} onClose={() => setTaxAddLineId(null)} onAdd={addTax} />
    </View>
  );
};

/* ── Add a GST tax slab (creates rates + groups in billing) ──────────── */
const AddTaxModal: React.FC<{ visible: boolean; onClose: () => void; onAdd: (pct: string) => Promise<void> }> = ({ visible, onClose, onAdd }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [pct, setPct] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (visible) { setPct(''); setBusy(false); } }, [visible]);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    await onAdd(pct);
    setBusy(false);
  };

  const p = parseFloat(pct) || 0;
  const canAdd = p > 0 && !busy;
  const half = (p / 2).toString();

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="Add tax"
      subtitle="Creates a GST slab (CGST+SGST and IGST) in billing"
      width={440}
      headerTone="accent"
      footer={
        <>
          <Pressable onPress={onClose} style={{ height: 38, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: 10, justifyContent: 'center', backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
            <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable
            onPress={submit}
            disabled={!canAdd}
            style={{
              height: 38, paddingHorizontal: 18, borderRadius: 10, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7,
              backgroundColor: canAdd ? colors.accent : colors.paper3, borderWidth: 1, borderColor: canAdd ? colors.transparent : colors.line,
              ...(canAdd ? web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: `0 6px 14px -4px ${colors.accent}88`, cursor: 'pointer' }) : web({ cursor: 'not-allowed' })),
            }}
          >
            <SkIcon name="plus" size={15} color={canAdd ? '#fff' : colors.ink4} strokeWidth={2.4} />
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: canAdd ? '#fff' : colors.ink4 }}>{busy ? 'Adding…' : 'Add tax'}</Text>
          </Pressable>
        </>
      }
    >
      <View style={{ gap: 14 }}>
        <CommonInput
          label="Total GST rate (%) *"
          value={pct}
          onChangeText={(t) => setPct(t.replace(/[^0-9.]/g, '').slice(0, 5))}
          placeholder="e.g. 18"
          leftIcon={<SkIcon name="percent" size={15} color={colors.ink4} strokeWidth={2} />}
          inputProps={{ keyboardType: 'decimal-pad' }}
        />

        {/* Quick-pick common slabs */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {[3, 5, 12, 18, 28].map((v) => {
            const sel = p === v;
            return (
              <Pressable
                key={v}
                onPress={() => setPct(String(v))}
                style={{ paddingVertical: 7, paddingHorizontal: 14, borderRadius: 999, borderWidth: 1, borderColor: sel ? colors.accentLine : colors.line, backgroundColor: sel ? colors.accentSoft : colors.card, ...web({ cursor: 'pointer' }) }}
              >
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: sel ? colors.accentInk : colors.ink2 }}>{v}%</Text>
              </Pressable>
            );
          })}
        </View>

        {/* Live breakdown */}
        {p > 0 && (
          <View style={{ borderRadius: 12, borderWidth: 1, borderColor: colors.line, overflow: 'hidden' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 11, paddingHorizontal: 13, backgroundColor: colors.paper2, borderBottomWidth: 1, borderBottomColor: colors.line }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: colors.accent }} />
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: colors.ink }}>Intra-state</Text>
              </View>
              <Text style={{ fontFamily: font.monoMedium, fontSize: 12.5, color: colors.ink2 }}>CGST {half}% + SGST {half}%</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 11, paddingHorizontal: 13 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: colors.neutral }} />
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: colors.ink }}>Inter-state</Text>
              </View>
              <Text style={{ fontFamily: font.monoMedium, fontSize: 12.5, color: colors.ink2 }}>IGST {p}%</Text>
            </View>
          </View>
        )}
      </View>
    </ModalShell>
  );
};

/* ── Inline CRM contact search (type to find, or add a new one) ──────── */
const ContactSearchField: React.FC<{
  type: ContactType;
  word: string;
  contact: { id: string; name: string } | null;
  optional?: boolean;
  onSelect: (c: Contact) => void;
  onClear: () => void;
  onAddNew: (prefillName: string) => void;
}> = ({ type, word, contact, optional, onSelect, onClear, onAddNew }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [query, setQuery] = useState('');
  const [all, setAll] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listContacts({ type })
      .then((list) => { if (alive) setAll(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setAll([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [type]);

  const q = query.trim().toLowerCase();
  const results = q
    ? all.filter((c) => (c.name + ' ' + (c.email || '') + ' ' + (c.gstin || '')).toLowerCase().includes(q))
    : all;

  // Selected state — premium chip with gradient avatar + Change action.
  if (contact) {
    const [g1, g2] = avatarOf(contact.name || word);
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 13,
          padding: 13,
          borderRadius: 13,
          borderWidth: 1,
          borderColor: colors.accentLine,
          backgroundColor: colors.accentSoft,
          ...web({ backgroundImage: `linear-gradient(135deg, ${colors.accentSoft}, ${colors.card})`, ...theme.shadow('sm') }),
        }}
      >
        {/* gradient avatar with ring + presence check badge */}
        <View style={{ width: 42, height: 42 }}>
          <View
            style={{
              width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center', backgroundColor: g2,
              ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 0 0 2px ${colors.card}, 0 4px 10px -2px ${g2}88` }),
            }}
          >
            <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 15, color: '#fff' }}>{initials(contact.name)}</Text>
          </View>
          <View style={{ position: 'absolute', right: -2, bottom: -2, width: 16, height: 16, borderRadius: 8, backgroundColor: colors.success, alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: colors.card }}>
            <SkIcon name="check" size={9} color="#fff" strokeWidth={3} />
          </View>
        </View>

        <View style={{ flex: 1, minWidth: 0 }}>
          <Text numberOfLines={1} style={{ fontFamily: font.sansBold, fontSize: 14.5, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>{contact.name}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 3 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 1, paddingHorizontal: 7, borderRadius: 999, backgroundColor: colors.successSoft, borderWidth: 1, borderColor: colors.successLine }}>
              <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.success }} />
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '600', color: colors.success, letterSpacing: 0.2 }}>SELECTED</Text>
            </View>
            <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink3 }}>Billing to this {word}</Text>
          </View>
        </View>

        <Pressable
          onPress={onClear}
          style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 6, height: 32, paddingHorizontal: 13, borderRadius: 9, borderWidth: 1, borderColor: colors.line, backgroundColor: pressed ? colors.paper2 : colors.card, ...web({ cursor: 'pointer', transition: 'background-color .12s ease' }) })}
        >
          <SkIcon name="refresh" size={13} color={colors.ink2} strokeWidth={2} />
          <Text style={{ fontFamily: font.sansMedium, fontSize: 12.5, fontWeight: '500', color: colors.ink2 }}>Change</Text>
        </Pressable>
      </View>
    );
  }

  // Search state — a search field that reveals a CRM dropdown + "add new".
  return (
    <View style={{ position: 'relative', zIndex: 20 }}>
      <View style={{ position: 'relative' }}>
        <View style={{ position: 'absolute', left: 12, top: 0, bottom: 0, justifyContent: 'center', zIndex: 1 }}>
          <SkIcon name="search" size={15} color={colors.ink4} strokeWidth={2} />
        </View>
        <TextInput
          value={query}
          onChangeText={setQuery}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 160)}
          placeholder={`Search ${word} by name, email or GSTIN…`}
          placeholderTextColor={colors.ink4}
          style={{
            height: 42, paddingLeft: 38, paddingRight: 12, borderWidth: 1,
            borderColor: focused ? colors.accent : colors.line,
            backgroundColor: colors.paper, borderRadius: 10, fontFamily: font.sans, fontSize: 13, color: colors.ink,
            ...web({ outlineStyle: 'none', transition: 'border-color .14s ease', boxShadow: focused ? `0 0 0 3px ${colors.accent}22` : 'none' }),
          }}
        />
      </View>

      {focused && (
        <View
          style={{
            position: 'absolute', top: 48, left: 0, right: 0, zIndex: 50,
            backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 12, overflow: 'hidden',
            ...theme.shadow('lg'),
          }}
        >
          <ScrollView style={{ maxHeight: 264 }} keyboardShouldPersistTaps="handled">
            {loading ? (
              <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3, padding: 16, textAlign: 'center' }}>Loading…</Text>
            ) : results.length === 0 ? (
              <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink4, padding: 16, textAlign: 'center' }}>
                {q ? `No ${word}s match “${query.trim()}”.` : `No ${word}s in your CRM yet.`}
              </Text>
            ) : (
              results.slice(0, 8).map((c) => {
                const [g1, g2] = avatarOf(c.name || word);
                return (
                  <Pressable
                    key={c.id}
                    onPress={() => { onSelect(c); setQuery(''); setFocused(false); }}
                    style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 10, paddingHorizontal: 12, borderBottomWidth: 1, borderColor: colors.line, backgroundColor: pressed ? colors.paper2 : 'transparent', ...web({ cursor: 'pointer' }) })}
                  >
                    <View style={{ width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: g2, ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})` }) }}>
                      <Text style={{ fontSize: 10, fontWeight: '700', color: '#fff', fontFamily: font.sansBold }}>{initials(c.name)}</Text>
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink }}>{c.name}</Text>
                      <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4 }}>{c.gstin ? `GSTIN ${c.gstin}` : c.email || c.phone || '—'}</Text>
                    </View>
                    <SkIcon name="chevRight" size={15} color={colors.ink4} strokeWidth={2} />
                  </Pressable>
                );
              })
            )}
          </ScrollView>
          {/* Add new */}
          <Pressable
            onPress={() => { onAddNew(query.trim()); setFocused(false); }}
            style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 12, backgroundColor: pressed ? colors.accentSoft : colors.paper2, ...web({ cursor: 'pointer' }) })}
          >
            <View style={{ width: 26, height: 26, borderRadius: 8, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, alignItems: 'center', justifyContent: 'center' }}>
              <SkIcon name="plus" size={14} color={colors.accentInk} strokeWidth={2.4} />
            </View>
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: colors.accentInk }}>
              {query.trim() ? `Add new ${word} “${query.trim()}”` : `Add a new ${word}`}
            </Text>
          </Pressable>
        </View>
      )}

      {optional && !focused && (
        <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4, marginTop: 5 }}>Optional — leave blank for a cash {word}.</Text>
      )}
    </View>
  );
};

/* ── Add a new customer/vendor (creates in CRM, then selects it) ─────── */
const AddCustomerModal: React.FC<{ type: ContactType; visible: boolean; prefillName?: string; onClose: () => void; onCreated: (c: Contact) => void }> = ({ type, visible, prefillName, onClose, onCreated }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const word = type === 'VENDOR' ? 'vendor' : 'customer';
  const Word = word.charAt(0).toUpperCase() + word.slice(1);
  const IN = COUNTRIES.find((c) => c.code === 'IN')!;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(true);
  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState<CountryEntry>(IN);
  const [gstin, setGstin] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [company, setCompany] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [created, setCreated] = useState<Contact | null>(null);

  useEffect(() => {
    if (visible) { setName(prefillName || ''); setEmail(''); setEmailValid(true); setPhone(''); setPhoneCountry(IN); setGstin(''); setStateCode(''); setCompany(''); setBusy(false); setConfirmOpen(false); setSuccessOpen(false); setCreated(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, prefillName]);

  // Save is enabled only once required + entered fields validate. GSTIN is
  // optional, but a typed one must be well-formed or CRM rejects the create.
  const emailOk = !email.trim() || emailValid;
  const gstinOk = !gstin.trim() || REGEX.GSTIN.test(gstin.trim().toUpperCase());
  // CRM needs at least one reachable identity — mirror it so Save can't 400.
  const canSave = !!name.trim() && (!!email.trim() || !!phone.trim()) && emailOk && gstinOk && !busy;

  const doCreate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const payload: CreateContactPayload = {
        name: name.trim(),
        type,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: `${phoneCountry.dialCode} ${phone.trim()}` } : {}),
        ...(gstin.trim() ? { gstin: gstin.trim().toUpperCase() } : {}),
        ...(stateCode.trim() ? { state_code: stateCode.trim() } : {}),
        ...(company.trim() ? { company: company.trim() } : {}),
      };
      const c = await createContact(payload, { silent: true }); // SuccessDialog is the confirmation here
      setCreated(c);
      setConfirmOpen(false);
      setBusy(false);
      setSuccessOpen(true);
    } catch {
      setBusy(false); // error toast shown by the service layer
      setConfirmOpen(false);
    }
  };

  return (
    <>
    <ModalShell
      visible={visible}
      onClose={onClose}
      title={`Add ${word}`}
      subtitle={type === 'VENDOR' ? 'Creates a vendor in CRM and uses it on this document' : 'Creates a customer in CRM and bills this document to it'}
      width={460}
      headerTone="accent"
      footer={
        <>
          <Pressable onPress={onClose} style={{ height: 38, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: 10, justifyContent: 'center', backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
            <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable
            onPress={() => canSave && setConfirmOpen(true)}
            disabled={!canSave}
            style={{
              height: 38, paddingHorizontal: 18, borderRadius: 10, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7,
              backgroundColor: canSave ? colors.accent : colors.paper3,
              borderWidth: 1, borderColor: canSave ? colors.transparent : colors.line,
              ...(canSave ? web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: `0 6px 14px -4px ${colors.accent}88`, cursor: 'pointer' }) : web({ cursor: 'not-allowed' })),
            }}
          >
            <SkIcon name="check" size={15} color={canSave ? '#fff' : colors.ink4} strokeWidth={2.4} />
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: canSave ? '#fff' : colors.ink4 }}>Save &amp; select</Text>
          </Pressable>
        </>
      }
    >
      <View style={{ gap: 14 }}>
        {/* Contact section */}
        <View style={{ gap: 11 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
            <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.accent }} />
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.8 }}>Contact</Text>
          </View>
          <CommonInput
            label={`${Word} name *`}
            value={name}
            onChangeText={setName}
            placeholder="e.g. Style Well Pvt Ltd"
            leftIcon={<SkIcon name="building" size={16} color={colors.ink4} strokeWidth={2} />}
          />
          <EmailComponent value={email} onValueChange={(e, valid) => { setEmail(e); setEmailValid(valid); }} />
          <MobileNumberInput
            label="Phone"
            value={phone}
            selectedCountry={phoneCountry}
            onCountryChange={setPhoneCountry}
            onChangeText={setPhone}
            placeholder="00000 00000"
          />
        </View>

        <View style={{ height: 1, backgroundColor: colors.line }} />

        {/* Tax & business section */}
        <View style={{ gap: 11 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
            <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.accent }} />
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.8 }}>Tax &amp; business</Text>
          </View>
          <CommonInput
            label="Company"
            value={company}
            onChangeText={setCompany}
            placeholder="Legal / trade name"
            leftIcon={<SkIcon name="building" size={16} color={colors.ink4} strokeWidth={2} />}
          />
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <View style={{ flex: 2 }}>
              <CommonInput
                label="GSTIN"
                value={gstin}
                onChangeText={(t) => setGstin(t.toUpperCase().slice(0, 15))}
                error={gstinOk ? undefined : 'Enter a valid 15-character GSTIN (e.g. 33AABCU9603R1ZM)'}
                placeholder="e.g. 33AABCU9603R1ZM"
                leftIcon={<SkIcon name="percent" size={15} color={colors.ink4} strokeWidth={2} />}
                inputProps={{ autoCapitalize: 'characters' }}
              />
            </View>
            <View style={{ flex: 1 }}>
              <CommonInput
                label="State code"
                value={stateCode}
                onChangeText={(t) => setStateCode(t.replace(/\D/g, '').slice(0, 2))}
                placeholder="33"
                inputProps={{ keyboardType: 'number-pad' }}
              />
            </View>
          </View>
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 10, borderRadius: 9, backgroundColor: colors.infoSoft, borderWidth: 1, borderColor: colors.infoLine }}>
          <SkIcon name="alert" size={14} color={colors.info} strokeWidth={2} />
          <Text style={{ flex: 1, fontFamily: font.sans, fontSize: 11, color: colors.ink2, lineHeight: 15 }}>
            State code sets the place of supply (33 = Tamil Nadu). Name plus an email or phone is required; GSTIN is optional.
          </Text>
        </View>
      </View>
    </ModalShell>

    {/* Confirm before creating */}
    <ConfirmDialog
      visible={confirmOpen}
      title={`Create this ${word}?`}
      message={`“${name.trim()}” will be added to your CRM and selected on this document. Do you want to continue?`}
      confirmText={`Yes, create ${word}`}
      cancelText="Not yet"
      confirmColor={colors.accent}
      busy={busy}
      onConfirm={doCreate}
      onCancel={() => (busy ? null : setConfirmOpen(false))}
    />

    {/* Success */}
    <SuccessDialog
      visible={successOpen}
      title={`${Word} created`}
      message={`${created?.name || name.trim()} is ready and selected on this ${word}.`}
      summary={[
        'Saved to your CRM',
        `Selected on this ${word}`,
        ...(gstin.trim() ? [`GSTIN ${gstin.trim().toUpperCase()}`] : []),
      ]}
      primaryAction={{ label: 'Done', onPress: () => { setSuccessOpen(false); if (created) onCreated(created); } }}
      onClose={() => { setSuccessOpen(false); if (created) onCreated(created); }}
    />
    </>
  );
};

/* ── Add / enable a currency (+ optional ₹ rate) ─────────────────────── */
const AddCurrencyModal: React.FC<{ visible: boolean; onClose: () => void; onAdd: (code: string, rate: string) => Promise<void> }> = ({ visible, onClose, onAdd }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [code, setCode] = useState('');
  const [rate, setRate] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (visible) { setCode(''); setRate(''); setBusy(false); } }, [visible]);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    await onAdd(code, rate);
    setBusy(false);
  };

  const canAdd = code.trim().length >= 3 && !busy;
  const rateInput = { flex: 1, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 9, height: 40, paddingHorizontal: 12, fontFamily: font.mono, fontSize: 13, color: colors.ink, ...web({ outlineStyle: 'none' }) };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="Add currency"
      subtitle="Enables it for this org (stored in billing)"
      width={460}
      headerTone="accent"
      footer={
        <>
          <Pressable onPress={onClose} style={{ height: 38, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: 10, justifyContent: 'center', backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
            <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable
            onPress={submit}
            disabled={!canAdd}
            style={{
              height: 38, paddingHorizontal: 18, borderRadius: 10, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7,
              backgroundColor: canAdd ? colors.accent : colors.paper3, borderWidth: 1, borderColor: canAdd ? colors.transparent : colors.line,
              ...(canAdd ? web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: `0 6px 14px -4px ${colors.accent}88`, cursor: 'pointer' }) : web({ cursor: 'not-allowed' })),
            }}
          >
            <SkIcon name="plus" size={15} color={canAdd ? '#fff' : colors.ink4} strokeWidth={2.4} />
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: canAdd ? '#fff' : colors.ink4 }}>{busy ? 'Adding…' : 'Add currency'}</Text>
          </Pressable>
        </>
      }
    >
      <View style={{ gap: 16 }}>
        <CommonInput
          label="Currency code *"
          value={code}
          onChangeText={(t) => setCode(t.toUpperCase().slice(0, 3))}
          placeholder="e.g. EUR"
          leftIcon={<SkIcon name="rupee" size={15} color={colors.ink4} strokeWidth={2} />}
          inputProps={{ autoCapitalize: 'characters' }}
        />
        <View>
          <Text style={{ fontFamily: font.sansMedium, fontSize: 12, color: colors.ink3, marginBottom: 6, fontWeight: '500' }}>Exchange rate to ₹ (optional)</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <View style={{ paddingHorizontal: 11, height: 40, borderRadius: 9, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.monoMedium, fontSize: 13, color: colors.ink2 }}>1 {code || 'XXX'} =</Text>
            </View>
            <TextInput value={rate} onChangeText={setRate} keyboardType="decimal-pad" placeholder="e.g. 91.50" placeholderTextColor={colors.ink4} style={rateInput} />
            <Text style={{ fontFamily: font.monoSemibold, fontSize: 15, color: colors.ink3 }}>₹</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 10, paddingVertical: 8, paddingHorizontal: 11, borderRadius: 9, backgroundColor: colors.infoSoft, borderWidth: 1, borderColor: colors.infoLine }}>
            <SkIcon name="alert" size={14} color={colors.info} strokeWidth={2} />
            <Text style={{ flex: 1, fontFamily: font.sans, fontSize: 11.5, color: colors.ink2, lineHeight: 16 }}>Needed to show the ₹ equivalent. You can set it later in Settings.</Text>
          </View>
        </View>
      </View>
    </ModalShell>
  );
};

const Row: React.FC<{ label: string; value: string; colors: any; font: any; muted?: boolean }> = ({ label, value, colors, font, muted }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
    <Text style={{ fontFamily: font.sans, fontSize: 12.5, color: muted ? colors.ink4 : colors.ink3 }}>{label}</Text>
    <Text style={{ fontFamily: font.monoMedium, fontSize: 12.5, color: muted ? colors.ink4 : colors.ink2 }}>{value}</Text>
  </View>
);

export default DocumentEditor;
